import { useEffect, useState } from 'react';

import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';
import { MeepMeepNode } from '../../../../types/MeepMeepNode';
import { reinstantiate } from '../../../../utils/nodeFactory';
import './ComponentCard.css';

interface Props {
    node: MeepMeepNode;
}

export function MeepMeepCard({ node }: Props) {
    const { onUpdate, isInteractionLocked } = useNodeOperations();
    const reinstantiatedNode = reinstantiate(node) as MeepMeepNode;
    const liveNode = typeof reinstantiatedNode?.update === 'function'
        ? reinstantiatedNode
        : new MeepMeepNode(
            node.id,
            node.title,
            node.config?.main_market,
            node.config?.signal,
            node.config?.latency_ms,
            node.config?.insample_days,
            node.config?.target_forward_ticks,
            node.config?.max_volume,
            node.config?.trade_margin,
            node.config?.trade_margin_regime,
            node.isManualRename
        );
    const { config } = liveNode;

    const [localLatency, setLocalLatency] = useState(String(config.latency_ms ?? 35));
    const [localInSampleDays, setLocalInSampleDays] = useState(String(config.insample_days ?? 3));
    const [localTargetForwardTicks, setLocalTargetForwardTicks] = useState(String(config.target_forward_ticks ?? 25));
    const [localMaxVolume, setLocalMaxVolume] = useState(String(config.max_volume ?? 1));
    const [localTradeMargin, setLocalTradeMargin] = useState(String(config.trade_margin ?? 0));
    const [localTradeMarginRegime, setLocalTradeMarginRegime] = useState(String(config.trade_margin_regime ?? 'static'));

    useEffect(() => {
        setLocalLatency(String(config.latency_ms ?? 35));
        setLocalInSampleDays(String(config.insample_days ?? 3));
        setLocalTargetForwardTicks(String(config.target_forward_ticks ?? 25));
        setLocalMaxVolume(String(config.max_volume ?? 1));
        setLocalTradeMargin(String(config.trade_margin ?? 0));
        setLocalTradeMarginRegime(String(config.trade_margin_regime ?? 'static'));
    }, [config]);

    const commitUpdate = (field: keyof typeof config, value: string) => {
        if (isInteractionLocked) return;

        const numVal = parseFloat(value);
        if (Number.isNaN(numVal)) return;

        onUpdate(liveNode.id, liveNode.update(liveNode.id, {
            config: {
                ...config,
                [field]: numVal,
            },
        }));
    };

    const commitSelectUpdate = (field: keyof typeof config, value: string) => {
        if (isInteractionLocked) return;

        onUpdate(liveNode.id, liveNode.update(liveNode.id, {
            config: {
                ...config,
                [field]: value,
            },
        }));
    };

    const stopPropagation = (e: React.UIEvent) => {
        e.stopPropagation();
    };

    return (
        <div className="internal-card-container" style={{ minWidth: '320px', maxWidth: '420px' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Strategy Settings
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.8rem', marginBottom: '0.8rem' }}>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">In-Sample Days</label>
                    <input
                        type="number"
                        step="1"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localInSampleDays}
                        onChange={e => setLocalInSampleDays(e.target.value)}
                        onBlur={e => commitUpdate('insample_days', e.target.value)}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Target Forward Tick</label>
                    <input
                        type="number"
                        step="1"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localTargetForwardTicks}
                        onChange={e => setLocalTargetForwardTicks(e.target.value)}
                        onBlur={e => commitUpdate('target_forward_ticks', e.target.value)}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.8rem', marginBottom: '0.8rem' }}>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Latency (ms)</label>
                    <input
                        type="number"
                        step="1"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localLatency}
                        onChange={e => setLocalLatency(e.target.value)}
                        onBlur={e => commitUpdate('latency_ms', e.target.value)}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Max Volume</label>
                    <input
                        type="number"
                        step="0.1"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localMaxVolume}
                        onChange={e => setLocalMaxVolume(e.target.value)}
                        onBlur={e => commitUpdate('max_volume', e.target.value)}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.8rem', marginBottom: '0.8rem' }}>
                <div className="flex flex-col gap-xs">
                    <label className="field-label">Trade Margin</label>
                    <input
                        type="number"
                        step="0.01"
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localTradeMargin}
                        onChange={e => setLocalTradeMargin(e.target.value)}
                        onBlur={e => commitUpdate('trade_margin', e.target.value)}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        disabled={isInteractionLocked}
                    />
                </div>

                <div className="flex flex-col gap-xs">
                    <label className="field-label">Margin Regime</label>
                    <select
                        className="input-number-premium pan-zoom-exclude nodrag"
                        value={localTradeMarginRegime}
                        onChange={e => {
                            setLocalTradeMarginRegime(e.target.value);
                            commitSelectUpdate('trade_margin_regime', e.target.value);
                        }}
                        onPointerDown={stopPropagation}
                        onMouseDown={stopPropagation}
                        disabled={isInteractionLocked}
                    >
                        <option value="static">Static</option>
                    </select>
                </div>
            </div>

            <div style={{ marginTop: '1rem', borderTop: '1px solid var(--surface-glass-border)', paddingTop: '0.8rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    MeepMeep Topology
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                        padding: '6px 10px',
                        background: config.main_market ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)',
                        borderRadius: '6px',
                        border: `1px solid ${config.main_market ? 'rgba(46, 204, 113, 0.2)' : 'rgba(231, 76, 60, 0.2)'}`,
                        fontSize: '0.8rem'
                    }}>
                        <span style={{ color: config.main_market ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                            {config.main_market ? '✓' : '✗'}
                        </span>
                        <span style={{ flex: 1 }}>Main Market</span>
                        <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>[AlphaBook]</span>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                        padding: '6px 10px',
                        background: config.signal ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)',
                        borderRadius: '6px',
                        border: `1px solid ${config.signal ? 'rgba(46, 204, 113, 0.2)' : 'rgba(231, 76, 60, 0.2)'}`,
                        fontSize: '0.8rem'
                    }}>
                        <span style={{ color: config.signal ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                            {config.signal ? '✓' : '✗'}
                        </span>
                        <span style={{ flex: 1 }}>Alpha Signal</span>
                        <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>[Regression]</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
